import { useEffect, useRef } from 'react'
import { MapPin, Layers } from 'lucide-react'

export default function MapDisplay({ flights, location, radius = 20 }) {
  const canvasRef = useRef(null)
  
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio
      canvas.height = canvas.offsetHeight * window.devicePixelRatio
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    }
    resize()
    window.addEventListener('resize', resize)
    
    const draw = () => {
      const w = canvas.offsetWidth, h = canvas.offsetHeight
      ctx.fillStyle = '#020617'; ctx.fillRect(0, 0, w, h)
      ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 0.5
      for (let x = 0; x < w; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke() }
      for (let y = 0; y < h; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke() }
      
      const cx = w / 2, cy = h / 2
      ctx.strokeStyle = '#0ea5e9'; ctx.setLineDash([4, 4])
      for (let r = 50; r < Math.min(w, h) / 2; r += 80) { ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke() }
      ctx.setLineDash([])
      ctx.fillStyle = '#0ea5e9'; ctx.beginPath(); ctx.arc(cx, cy, 6, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(cx, cy, 12, 0, Math.PI * 2); ctx.stroke()
      
      // Aircraft are placed by their true bearing/distance from the viewer's
      // location (f.bearing, f.distance), scaled to fit the configured radius
      // within the canvas. The icon itself is rotated by the aircraft's own
      // true_track (the direction it's flying) - a different angle from its
      // bearing from the viewer.
      const pxPerNm = (Math.min(w, h) / 2 - 30) / radius
      flights.forEach((f, i) => {
        if (f.latitude == null || f.longitude == null || f.distance == null) return
        const placementAngle = (f.bearing || 0) * Math.PI / 180
        const dist = Math.min(f.distance, radius) * pxPerNm
        const fx = cx + Math.sin(placementAngle) * dist, fy = cy - Math.cos(placementAngle) * dist
        const headingAngle = (f.true_track || 0) * Math.PI / 180
        ctx.strokeStyle = `rgba(6, 182, 212, ${0.1 + (i % 3) * 0.1})`; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(fx, fy); ctx.stroke()
        ctx.save(); ctx.translate(fx, fy); ctx.rotate(headingAngle)
        ctx.fillStyle = f.altitude > 30000 ? '#f59e0b' : f.altitude > 10000 ? '#10b981' : '#f43f5e'
        ctx.beginPath(); ctx.moveTo(0, -8); ctx.lineTo(-5, 6); ctx.lineTo(0, 3); ctx.lineTo(5, 6); ctx.closePath(); ctx.fill()
        ctx.restore()
        ctx.fillStyle = '#94a3b8'; ctx.font = '10px monospace'; ctx.fillText(f.callsign || '---', fx + 10, fy - 5)
      })
    }

    draw()
    const anim = setInterval(draw, 1000)
    return () => { clearInterval(anim); window.removeEventListener('resize', resize) }
  }, [flights, location, radius])
  
  return (
    <div className="h-screen w-screen bg-slate-950 relative">
      <canvas ref={canvasRef} className="w-full h-full" />
      <div className="absolute top-6 left-6 glass-panel p-4">
        <div className="flex items-center gap-2 text-flight-cyan mb-2"><Layers className="w-4 h-4" /><span className="text-sm font-bold">RADAR VIEW</span></div>
        <div className="text-white font-mono text-2xl">{flights.length} <span className="text-slate-500 text-sm">contacts</span></div>
        <div className="text-slate-400 text-xs mt-1">Range: {radius} NM</div>
      </div>
      <div className="absolute bottom-6 right-6 glass-panel p-4">
        <div className="text-slate-400 text-xs mb-2 tracking-wider">ALTITUDE</div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Cruise (&gt;30k)</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Medium (10-30k)</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> Low (&lt;10k)</span>
        </div>
      </div>
      <div className="absolute bottom-6 left-6 glass-panel px-3 py-2 flex items-center gap-2">
        <MapPin className="w-3 h-3 text-flight-cyan" />
        <span className="text-slate-300 text-xs font-mono">{location.lat?.toFixed(4)}°, {location.lon?.toFixed(4)}°</span>
      </div>
    </div>
  )
}
